import { Component, AfterViewInit, HostListener, ViewChild, ElementRef, OnInit } from '@angular/core';
import { StateService } from '../Common/States/States.Service';
import { AssessorCollectorService } from '../Assessor-Collector/assessor.collector.service';
import { UpgradeNavigationServiceHandler } from '../Common/Routing/upgrade-navigation-handler.service';
import { StateSummary } from '../Common/States/state.model';
import { UserService } from '../Account/user.service';
import { TimerService } from '../UI-Lib/Utilities';

import * as _ from 'lodash';

declare const saveAs: any;
declare const Datamap: any;
declare const d3: any;

export interface StateTypeahead extends StateSummary {
    typeaheadVal: string;
}

@Component({
    selector: 'united-states',
    templateUrl: './states.component.html',
    styleUrls: ['./states.component.scss'],
    standalone: false,
})
export class UnitedStatesComponent implements OnInit, AfterViewInit {
    constructor(
        private readonly _stateService: StateService,
        private readonly _assessorCollectorService: AssessorCollectorService,
        private readonly _upgradeNavigationService: UpgradeNavigationServiceHandler,
        private readonly _userService: UserService,
        private readonly _timerService: TimerService,
    ) {}

    @ViewChild('stateContainer', { static: true }) stateContainer: ElementRef;
    @ViewChild('unitedStates', { static: true }) unitedStates: ElementRef;

    states: StateTypeahead[];
    loadingCollectorList: boolean = false;
    loadingAssessorList: boolean = false;
    disableCollectorList: boolean = false;
    disableAssessorList: boolean = false;
    selected: string;

    private _map: any;
    private _mapHeight: any;
    private _navigatedFrom: string;
    private readonly _NAVIGATED_DASHBOARD: string = 'dashboard';

    @HostListener('window:resize', ['$event']) onResize(event) {
        this._resizeMap();
    }

    ngOnInit(): void {
        if (this._userService.getUser().isDemoUser) {
            this.disableCollectorList = true;
            this.disableAssessorList = true;
        }

        this._navigatedFrom = this._stateService.navigatedFrom;
        this._stateService.navigatedFrom = null;

        this._getStates();
    }

    ngAfterViewInit(): void {
        this._timerService.setTimeout(() => {
            this._resizeMap();
        }, 100);
    }

    async downloadAssessorExtract(): Promise<void> {
        this.loadingAssessorList = true;

        try {
            const result: Blob = await this._assessorCollectorService.getAssessorExtract();
            this._downloadFile(result, 'Assessor');
        } finally {
            this.loadingAssessorList = false;
        }
    }

    async downloadCollectorExtract(): Promise<void> {
        this.loadingCollectorList = true;

        try {
            const result: Blob = await this._assessorCollectorService.getCollectorExtract();
            this._downloadFile(result, 'Collector');
        } finally {
            this.loadingCollectorList = false;
        }
    }

    onStateSelect(e: any): void {
        this._upgradeNavigationService.go('state', { stateId: e.item.stateID });
    }

    private async _getStates(): Promise<void> {
        const states = await this._stateService.getSummary();
        this.states = _.map(states, (state: StateTypeahead) => {
            // Hack since ngx-bootstrap doesn't currently support multiple typeaheadOptionFields
            state.typeaheadVal = `${state.fullName} ${state.abbr}`;
            return state;
        });
    }

    private _downloadFile(result: Blob, entity: string): void {
        const blob = new Blob([result], { type: 'text/csv' });

        saveAs(blob, `${entity} List Extract.csv`);
    }

    private _resizeMap(): void {
        const map = this.unitedStates.nativeElement;

        if (map) {
            map.innerHTML = '';
            this._mapHeight = this.unitedStates.nativeElement.clientHeight;
            this._updateMap();
        }
    }

    private _updateMap(): void {
        let width = window.innerWidth - 32; // 16px padding on each side
        let height = width * 0.55;
        if (height > this._mapHeight) {
            // set width and height to 11:6 aspect ratio
            width = this._mapHeight * 1.83;
            height = this._mapHeight;
        }

        this._map = new Datamap({
            scope: 'usa',
            element: this.unitedStates.nativeElement,
            geographyConfig: {
                popupOnHover: false,
                highlightFillColor: 'var(--ace-action)',
                highlightBorderColor: 'var(--ace-neutral-white)',
                highlightBorderWidth: 1,
            },
            fills: {
                defaultFill: 'var(--ace-neutral-300)',
            },
            labelSize: 14,
            width,
            height,
            responsive: false,
            done: (datamap) => {
                // Datamaps bug workaround: reapply event listeners after initial rendering
                const subunits = datamap.svg.selectAll('.datamaps-subunit');
                subunits
                    .on('mouseover', function () {
                        const $this = d3.select(this);

                        const previousAttributes = {
                            fill: $this.style('fill'),
                            stroke: $this.style('stroke'),
                            'stroke-width': $this.style('stroke-width'),
                            'fill-opacity': $this.style('fill-opacity'),
                        };

                        $this
                            .style('fill', 'var(--ace-action)')
                            .style('stroke', 'var(--ace-neutral-white)')
                            .style('stroke-width', 1)
                            .style('stroke-opacity', 1)
                            .style('fill-opacity', 1)
                            .attr('data-previousAttributes', JSON.stringify(previousAttributes));
                    })
                    .on('mouseout', function () {
                        const $this = d3.select(this);

                        // Reapply previous attributes
                        const previousAttributes = JSON.parse($this.attr('data-previousAttributes'));
                        for (const attr in previousAttributes) {
                            $this.style(attr, previousAttributes[attr]);
                        }
                        $this.on('mousemove', null);
                        d3.selectAll('.datamaps-hoverover').style('display', 'none');
                    })
                    .on('click', (geography) => {
                        const state: StateTypeahead = _.find(this.states, {
                            abbr: geography.id,
                        });

                        if (state) {
                            this._upgradeNavigationService.go('state', { stateId: state.stateID });
                        } else {
                            console.warn('State not found!');
                        }
                    });
            },
        });

        this._map.labels({ labelColor: '#3E4050', fontSize: 14 });
    }
}
